import { Alert, Button, Group, NumberInput, Progress, Select, Stack, Text, TextInput, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { useQuery } from '@tanstack/react-query'
import { IconAlertTriangle } from '@tabler/icons-react'
import {
  SERIES, formatearNumero, pagoEntrada,
  type Empresa, type Pago, type PagoEntrada, type SaldoOrden,
} from '@serkat/shared'
import { api } from '../../../api/cliente'
import { hoyLocal, moneda } from '../../../compartido/formato'
import { TablaItemsPago } from './TablaItemsPago'

interface Props {
  inicial?: Pago
  guardando: boolean
  onGuardar: (datos: PagoEntrada) => void
  onCancelar: () => void
}

export function FormularioPago({ inicial, guardando, onGuardar, onCancelar }: Props) {
  const { data: empresas } = useQuery({
    queryKey: ['empresas', 'activas'],
    queryFn: () => api.get<Empresa[]>('/empresas'),
  })

  // Solo órdenes aprobadas con saldo: no se paga lo que aún puede cambiar.
  const { data: ordenes } = useQuery({
    queryKey: ['pagos', 'ordenes-con-saldo'],
    queryFn: () => api.get<SaldoOrden[]>('/pagos/ordenes-con-saldo'),
    enabled: !inicial,
  })

  const { data: propuesto } = useQuery({
    queryKey: ['pagos', 'siguiente-numero'],
    queryFn: () => api.get<{ correlativo: number }>('/pagos/siguiente-numero'),
    enabled: !inicial,
    staleTime: 0,
  })

  const form = useForm<PagoEntrada>({
    initialValues: {
      empresaId: inicial?.empresaId ?? 0,
      correlativo: inicial?.correlativo ?? 0,
      ordenId: inicial?.ordenId ?? '',
      fechaEmision: inicial?.fechaEmision ?? hoyLocal(),
      numeroFactura: inicial?.numeroFactura ?? null,
      fechaFactura: inicial?.fechaFactura ?? null,
      componente: inicial?.componente ?? null,
      actividad: inicial?.actividad ?? null,
      numeroArmada: inicial?.numeroArmada ?? 1,
      totalArmadas: inicial?.totalArmadas ?? null,
      monto: inicial?.monto ?? 0,
      observaciones: inicial?.observaciones ?? null,
      items: inicial?.items.map((i) => ({
        itemNumero: i.itemNumero, ordenItemId: i.ordenItemId, descripcion: i.descripcion,
        cantidadSolicitada: i.cantidadSolicitada, cantidadRecibida: i.cantidadRecibida,
        conforme: i.conforme,
      })) ?? [],
    },
    validate: zodResolver(pagoEntrada),
  })

  if (!inicial) {
    if (propuesto && form.values.correlativo === 0) {
      form.setFieldValue('correlativo', propuesto.correlativo)
    }
  }

  const seleccionada = ordenes?.find((o) => o.ordenId === form.values.ordenId)
  // Al editar, lo disponible incluye lo que este mismo pago ya comprometía.
  const disponible = inicial ? inicial.ordenSaldo + inicial.monto : (seleccionada?.saldo ?? 0)
  const codigoMoneda = (inicial?.moneda ?? seleccionada?.moneda ?? 'PEN') as 'PEN' | 'USD'
  const excede = form.values.monto > disponible + 0.005

  const numero = form.values.correlativo
    ? formatearNumero(SERIES.APROBACION_PAGO, form.values.correlativo)
    : '—'

  function elegirOrden(v: string | null) {
    form.setFieldValue('ordenId', v ?? '')
    const o = ordenes?.find((x) => x.ordenId === v)
    if (o) {
      // Propone el saldo completo y la armada siguiente: el caso más común es
      // saldar, y si es parcial el usuario baja el monto.
      form.setFieldValue('monto', o.saldo)
      form.setFieldValue('numeroArmada', o.armadasEmitidas + 1)
    }
  }

  return (
    <form onSubmit={form.onSubmit(onGuardar)}>
      <Stack gap="sm">
        {inicial ? (
          <Alert color="gray" variant="light">
            Orden <strong>{inicial.ordenNumero}</strong> · {inicial.proveedor}
          </Alert>
        ) : (
          <Select
            label="Orden a pagar"
            description="Solo órdenes aprobadas con saldo pendiente"
            withAsterisk
            searchable
            data={(ordenes ?? []).map((o) => ({
              value: o.ordenId,
              label: `${o.ordenNumero} — saldo ${moneda(o.saldo, o.moneda as 'PEN' | 'USD')}`,
            }))}
            value={form.values.ordenId || null}
            onChange={elegirOrden}
            error={form.errors['ordenId']}
            nothingFoundMessage="No hay órdenes aprobadas con saldo"
          />
        )}

        {(seleccionada || inicial) && (
          <div>
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed">
                Comprometido de la orden
              </Text>
              <Text size="xs" ff="monospace">
                {moneda(disponible - form.values.monto, codigoMoneda)} quedarían disponibles
              </Text>
            </Group>
            <Progress
              value={disponible > 0 ? Math.min(100, (form.values.monto / disponible) * 100) : 0}
              color={excede ? 'red' : 'petroleo'}
              size="sm"
            />
          </div>
        )}

        {excede && (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            El monto supera el saldo disponible de {moneda(disponible, codigoMoneda)}.
            El sistema lo rechazará al guardar.
          </Alert>
        )}

        <Group grow align="flex-start">
          <Select
            label="Empresa emisora" withAsterisk
            placeholder="Elija la empresa que emite"
            description="Se elige en cada documento; no hay una predeterminada" 
            data={(empresas ?? []).map((e) => ({ value: String(e.id), label: e.razonSocial }))}
            value={form.values.empresaId ? String(form.values.empresaId) : null}
            onChange={(v) => form.setFieldValue('empresaId', Number(v))}
            error={form.errors['empresaId']}
          />
          <NumberInput label="Correlativo" description={`Número: ${numero}`} withAsterisk
            min={1} hideControls {...form.getInputProps('correlativo')} />
          <TextInput label="Fecha de emisión" type="date" withAsterisk {...form.getInputProps('fechaEmision')} />
        </Group>

        <Group grow align="flex-start">
          <NumberInput
            label="Monto a pagar" withAsterisk min={0.01} decimalScale={2} hideControls thousandSeparator
            {...form.getInputProps('monto')}
          />
          <NumberInput label="N.º de armada" min={1} hideControls {...form.getInputProps('numeroArmada')} />
          <NumberInput
            label="Total de armadas" description="Opcional, si el pago es parcializado"
            min={1} hideControls
            value={form.values.totalArmadas ?? ''}
            onChange={(v) => form.setFieldValue('totalArmadas', v === '' ? null : Number(v))}
            error={form.errors['numeroArmada']}
          />
        </Group>

        <Group grow align="flex-start">
          <TextInput label="N.º de factura del proveedor" value={form.values.numeroFactura ?? ''}
            onChange={(e) => form.setFieldValue('numeroFactura', e.currentTarget.value || null)} />
          <TextInput label="Fecha de factura" type="date" value={form.values.fechaFactura ?? ''}
            onChange={(e) => form.setFieldValue('fechaFactura', e.currentTarget.value || null)} />
        </Group>

        <Group grow align="flex-start">
          <TextInput label="Componente" placeholder="Expediente técnico"
            value={form.values.componente ?? ''}
            onChange={(e) => form.setFieldValue('componente', e.currentTarget.value || null)} />
          <TextInput label="Actividad" placeholder="Plan de monitoreo arqueológico"
            value={form.values.actividad ?? ''}
            onChange={(e) => form.setFieldValue('actividad', e.currentTarget.value || null)} />
        </Group>

        <TablaItemsPago
          items={form.values.items}
          errores={form.errors as Record<string, string | undefined>}
          onCambiar={(items) => form.setFieldValue('items', items)}
        />

        <Textarea label="Observaciones" autosize minRows={2}
          placeholder="Forma de pago, condiciones, cronograma de armadas"
          value={form.values.observaciones ?? ''}
          onChange={(e) => form.setFieldValue('observaciones', e.currentTarget.value || null)} />

        <Group justify="space-between" mt="xs">
          <Text size="xs" c="dimmed">
            Se emite en estado Pendiente. Proveedor y moneda se heredan de la orden.
          </Text>
          <Group>
            <Button variant="default" onClick={onCancelar}>Cancelar</Button>
            <Button type="submit" loading={guardando}>Guardar</Button>
          </Group>
        </Group>
      </Stack>
    </form>
  )
}
